/**
 * Spanish language strings for the Vivatis PDB application.
 * All user-facing text should be defined here for easy localisation.
 */

const es = {
  /* ------------------------------------------------------------------ */
  /* Global / shared                                                    */
  /* ------------------------------------------------------------------ */
  common: {
    products: 'Productos',
    product: 'producto',
    productsPlural: 'productos',
    partner: 'Socio',
    name: 'Nombre',
    category: 'Categoría',
    yes: 'Sí',
    no: 'No',
    back: 'Atrás',
    next: 'Siguiente',
    skip: 'Omitir',
    filter: 'Filtrar',
    filterMode: 'Modo de filtro',
    and: 'Y',
    or: 'O',
    clearAll: 'Borrar todo',
    removeAll: 'Eliminar todo',
    browseProducts: 'Explorar productos',
    noProductsFound: 'No se encontraron productos.',
    noResultsFor: (term: string) => `Sin resultados para «${term}».`,
    new: 'NUEVO',
    pdf: 'PDF',
    viewAll: 'Ver todo',
    offlineBanner: 'Estás sin conexión. Algunas funciones están restringidas.',
    offlineTooltip: 'No disponible en modo sin conexión',
    offlineLanguageHint: 'Sin conexión: el idioma de la interfaz cambia, los datos de producto permanecen en el idioma cargado anteriormente hasta que vuelvas a estar en línea',
    pwaUpdateAvailable: 'Una nueva versión está disponible. Actualizando...',
    pwaInstallTitle: 'Instalar app',
    pwaInstallDescription: 'Añadir a la pantalla de inicio para acceso rápido.',
    pwaInstallButton: 'Instalar',
  },

  /* ------------------------------------------------------------------ */
  /* Navigation                                                         */
  /* ------------------------------------------------------------------ */
  nav: {
    home: 'Inicio',
    products: 'Productos',
    wizard: 'Asistente',
    favorites: 'Favoritos',
    compare: 'Comparar',
    cart: 'Documentos',
    searchPlaceholder: 'Buscar productos...',
    toggleTheme: 'Cambiar tema',
    logout: 'Cerrar sesión',
    admin: 'Admin',
    toggleMenu: 'Abrir menú',
  },

  /* ------------------------------------------------------------------ */
  /* Branding                                                           */
  /* ------------------------------------------------------------------ */
  brand: {
    vivatis: 'VIVATIS',
    pdb: 'PDB',
    pharma: 'PHARMA',
    productDatabase: 'Base de datos de productos',
    copyright: 'Vivatis Pharma GmbH © 2026',
  },

  /* ------------------------------------------------------------------ */
  /* Login                                                              */
  /* ------------------------------------------------------------------ */
  login: {
    email: 'Correo electrónico',
    password: 'Contraseña',
    showPassword: 'Mostrar contraseña',
    hidePassword: 'Ocultar contraseña',
    signingIn: 'Iniciando sesión...',
    signIn: 'Iniciar sesión',
    forgotPassword: '¿Olvidaste tu contraseña?',
  },

  /* ------------------------------------------------------------------ */
  /* Home page                                                          */
  /* ------------------------------------------------------------------ */
  home: {
    welcomeBack: (name: string) => `¡Bienvenido de nuevo${name ? `, ${name}` : ''}!`,
    whatToDo: '¿Qué le gustaría hacer hoy?',
    stats: {
      products: 'Productos',
      new: 'Nuevo',
      favorites: 'Favoritos',
      inCart: 'En documento',
    },
    actions: {
      browseProducts: 'Explorar productos',
      viewAllProducts: (count: number) => (count === 1 ? 'Ver 1 producto' : `Ver los ${count} productos`),
      browseProductsCount: (count: number) => `${count} productos`,
      loading: 'Cargando...',
      startWizard: 'Iniciar asistente',
      findMatching: 'Buscar productos coincidentes',
      favorites: 'Favoritos',
      productsSaved: (count: number) => `${count} guardados`,
      meetingCart: 'Documento de reunión',
      productsInCart: (count: number) => `${count} en el documento`,
    },
    browseByIndication: 'Explorar por indicación',
    topIndications: 'Sus indicaciones principales',
    topIndicationsEmpty: 'Comience a explorar productos para ver aquí sus indicaciones más vistas.',
    newProducts: 'Productos nuevos',
    newProductsTooltip: 'Todos los productos añadidos en los últimos 12 meses se consideran nuevos.',
    newStudies: 'Nuevos estudios',
    newStudiesTooltip: 'Productos con estudios clínicos publicados recientemente en los últimos 12 meses.',
    newStudyBadge: 'Nuevo estudio',
    publishedStudies: (count: number) => `${count} estudios clínicos publicados`,
    favoritesTitle: 'Favoritos',
    noFavorites: 'Aún no hay favoritos. Haga clic en el icono del corazón de cualquier producto para guardarlo aquí.',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'producto' : 'productos'}`,
    recentlyViewed: 'Vistos recientemente',
    noRecentlyViewed: 'Aún no ha visto ningún producto. Comience con el',
    productCatalog: 'Catálogo de productos',
    viewAll: 'Ver todo',
  },

  /* ------------------------------------------------------------------ */
  /* Product list                                                       */
  /* ------------------------------------------------------------------ */
  productList: {
    sort: {
      relevance: 'Relevancia',
      nameAsc: 'Nombre (A-Z)',
      nameDesc: 'Nombre (Z-A)',
      articleNumber: 'N.º de artículo',
      partner: 'Socio',
      indication: 'Indicación',
    },
    viewTable: 'Tabla',
    viewCards: 'Tarjetas',
    productsFound: (count: number) => `${count} productos encontrados`,
    productCount: (count: number, total: number) => `${count} de ${total} productos`,
    loading: 'Cargando productos...',
    adjustFilters: 'Ajuste sus filtros.',
    resetFilters: 'Restablecer filtros',
  },

  /* ------------------------------------------------------------------ */
  /* Product search                                                     */
  /* ------------------------------------------------------------------ */
  productSearch: {
    placeholder: 'Buscar por nombre, número de artículo, ingrediente, indicación...',
  },

  /* ------------------------------------------------------------------ */
  /* Product table                                                      */
  /* ------------------------------------------------------------------ */
  productTable: {
    articleNo: 'N.º de artículo',
    name: 'Nombre',
    partner: 'Socio',
    indication: 'Indicación',
    application: 'Aplicación',
    country: 'País',
    dailyDose: 'Dosis diaria',
    claims: 'Declaraciones',
    actions: 'Acciones',
  },

  /* ------------------------------------------------------------------ */
  /* Product detail                                                     */
  /* ------------------------------------------------------------------ */
  productDetail: {
    tabs: {
      overview: 'Resumen',
      productData: 'Datos del producto',
      moreDetails: 'Precio',
    },
    notFound: 'Producto no encontrado',
    backToOverview: 'Volver al resumen',
    marketingClaims: 'Declaraciones de marketing',
    indications: 'Indicaciones',
    applications: 'Aplicaciones',
    countries: 'Países',
    description: 'Descripción',
    shortDescription: 'Descripción corta',
    website: 'Sitio web del producto',
    multimedia: 'Sitio web multimedia',
    studies: 'Estudios',
    origin: 'Origen',
    dosage: 'Dosificación',
    appearance: 'Aspecto',
    category: 'Categoría',
    articleNumber: 'Número de artículo',
    active: 'Activo',
    created: 'Creado',
    showDailyPrice: 'Mostrar precio diario',
    perDay: '/ día',
    generatingPdf: 'Generando PDF...',
    sendViaEmail: 'Enviar por correo electrónico',
    emailSubject: (name: string, companyName: string) => `${companyName} — Ficha técnica del producto: ${name}`,
    emailBody: (name: string, userName: string, companyName: string) =>
      `Estimado cliente,\n\nAdjunto encontrará la ficha técnica del producto ${name}.\n\nNo dude en contactarme si tiene alguna pregunta o necesita información adicional.\n\nAtentamente,\n${userName}\n${companyName}`,
    toastEmail: '¡Correo electrónico enviado con éxito!',
  },

  /* ------------------------------------------------------------------ */
  /* Actions (favorite, cart, compare)                                   */
  /* ------------------------------------------------------------------ */
  actions: {
    addToFavorites: 'Añadir a favoritos',
    removeFromFavorites: 'Eliminar de favoritos',
    favorited: 'En favoritos',
    favorite: 'Favorito',
    addToCart: 'Añadir al documento',
    removeFromCart: 'Eliminar del documento',
    removeFromAllCarts: 'Eliminar de todos los documentos',
    inCart: 'En el documento',
    addToCartLabel: 'Añadir al documento',
    inCartLabel: 'En el documento',
    cartLabel: 'Documentos',
    addToComparison: 'Añadir a la comparación',
    removeFromComparison: 'Eliminar de la comparación',
    inComparison: 'En la comparación',
    compared: 'Comparado',
    compare: 'Comparar',
    manageFavorites: 'Gestionar favoritos',
    addToFavoritesNoGroup: 'Añadir a favoritos (sin grupo)',
    favoritesLabel: 'Favoritos',
  },

  /* ------------------------------------------------------------------ */
  /* Favorites page                                                     */
  /* ------------------------------------------------------------------ */
  favorites: {
    title: 'Favoritos',
    noFavorites: 'Sin favoritos',
    noFavoritesDescription: 'Marque productos como favoritos para verlos aquí.',
    deleteGroup: {
      title: 'Eliminar grupo',
      message: (name: string) => `¿Está seguro de que desea eliminar "${name}"?`,
      moveLabel: 'Mover productos a:',
      ungroupedOption: 'Sin grupo (no agrupado)',
      confirm: 'Eliminar grupo',
      cancel: 'Cancelar',
    },
    browseProducts: 'Explorar productos',
    dragProductsHint: 'Arrastre productos aquí',
    notInGroup: 'Sin grupo',
    notInGroupWithCount: (count: number) => `Sin grupo (${count})`,
    dragCopyHint: '⌘/⇧ mantenido — suelte para copiar al grupo',
    dragMoveHint: 'Suelte para mover · mantenga ⌘/⇧ para copiar',
    addGroupToDocument: 'Añadir grupo al documento',
    deleteGroupTitle: 'Eliminar grupo',
    groupNamePlaceholder: 'Nombre del grupo...',
    create: 'Crear',
    cancel: 'Cancelar',
    newGroup: 'Nuevo grupo',
    inDocument: 'En el documento',
    alreadyInDocument: 'Los productos ya están en el documento.',
    addedToDocument: 'Productos añadidos al documento.',
    removedFromDocument: 'Productos eliminados del documento.',
    addToDocument: 'Añadir',
    productCountLabel: (count: number) => `${count} ${count === 1 ? 'producto' : 'productos'}`,
    openProductTitle: 'Abrir',
  },

  /* ------------------------------------------------------------------ */
  /* Comparison page                                                    */
  /* ------------------------------------------------------------------ */
  comparison: {
    title: 'Comparación de productos',
    loading: 'Cargando productos...',
    productCount: (count: number, max: number) => `(${count}/${max})`,
    noProducts: 'No hay productos para comparar',
    noProductsDescription: 'Añada productos para compararlos uno al lado del otro.',
    browseProducts: 'Explorar productos',
    addProduct: 'Añadir producto',
    removeFromComparison: 'Eliminar de la comparación',
    removeAll: 'Eliminar todo',
    showDifferencesOnly: 'Mostrar solo diferencias',
    showingDifferencesOnly: 'Mostrando solo diferencias',
    hideDailyPrice: 'Ocultar precio diario',
    dailyPriceHidden: 'Precio diario oculto',
    hidePrice: 'Ocultar precio',
    priceHidden: 'Precio oculto',
    allIdentical: 'Todos los valores son idénticos en estos productos.',
    rows: {
      articleName: 'Nombre del artículo',
      articleNumber: 'Número de artículo',
      partner: 'Socio',
      manufacturer: 'Fabricante',
      category: 'Categoría',
      clinicalStudies: 'Estudios clínicos',
      bioactiveIngredients: 'Ingredientes bioactivos / Dosificación',
      indications: 'Indicaciones',
      applications: 'Aplicaciones',
      countries: 'Países',
      dosage: 'Dosificación',
      dailyDosage: 'Dosis diaria',
      appearance: 'Aspecto',
      origin: 'Origen',
      sourceOrigin: 'Fuente / Origen',
      marketingClaims: 'Declaraciones de marketing',
      dailyPrice: 'Precio diario',
      price: 'Precio',
      novelFood: 'Novel Food',
    },
    novelFoodStatuses: {
      yes: 'Sí',
      ongoing: 'En curso',
      approved: 'Aprobado',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Cart page                                                          */
  /* ------------------------------------------------------------------ */
  cart: {
    title: 'Documento de reunión',
    subtitle: 'Productos para su reunión con el cliente',
    emptyTitle: 'Su documento está vacío',
    emptyDescription: 'Añada productos del catálogo.',
    moveUp: 'Mover arriba',
    moveDown: 'Mover abajo',
    remove: 'Eliminar',
    rename: 'Renombrar',
    lockedNotice: (date: string) => `Este documento se envió el ${date} y ya no se puede modificar.`,
    generatePdf: 'Generar PDF',
    generatingPdf: 'Generando PDF...',
    sendViaEmail: 'Enviar por correo electrónico',
    createOpportunity: 'Crear oportunidad en SalesForce',
    clearCart: 'Vaciar documento',
    archiveDocument: 'Archivar documento',
    confirmArchive: (name: string) => `¿Archivar "${name}"? Ya no aparecerá en su lista de documentos y después solo será visible para los administradores.`,
    deleteDocument: 'Eliminar documento',
    confirmClear: '¿Está seguro de que desea vaciar el documento?',
    confirmDelete: (name: string) => `¿Eliminar "${name}"? Se eliminará el documento y todos sus productos.`,
    toastPdf: 'El PDF se ha generado y descargado.',
    toastPdfError: 'Error al generar el PDF.',
    toastEmail: '¡Correo electrónico enviado con éxito!',
    showDocuments: 'Mostrar documentos',
    newDocument: 'Nuevo documento',
    searchDocuments: 'Buscar documentos...',
    noDocumentsFound: 'No se encontraron documentos.',
    items: 'elementos',
    comingSoon: 'Próximamente',
    comingSoonDescription: 'La integración con SalesForce está actualmente en desarrollo. Esta función estará disponible en una futura actualización.',
    productCount: (count: number) => `${count} ${count === 1 ? 'producto' : 'productos'}`,
    emailSubject: (count: number, companyName: string) => `${companyName} — Selección de productos (${count} productos)`,
    emailBody: (productList: string, userName: string, companyName: string) => `Estimado cliente,\n\nA continuación encontrará la selección de productos de nuestra última reunión:\n\n${productList}\n\nNo dude en contactarme si tiene alguna pregunta o necesita información adicional sobre alguno de estos productos.\n\nAtentamente,\n${userName}\n${companyName}`,
    email: {
      title: 'Enviar por correo electrónico',
      recipients: 'Destinatarios',
      subject: 'Asunto',
      message: 'Mensaje',
      placeholder: 'Introducir dirección de correo...',
      cancel: 'Cancelar',
      send: 'Enviar correo',
      sending: 'Enviando...',
      invalidEmail: 'Por favor, introduzca una dirección de correo electrónico válida.',
      duplicateEmail: 'Esta dirección de correo electrónico ya ha sido añadida.',
      sendError: 'No se pudo enviar el correo electrónico. Por favor, inténtelo de nuevo.',
      products: 'Productos',
      productsCount: (selected: number, total: number) => `${selected} de ${total}`,
      selectAll: 'Seleccionar todo',
      deselectAll: 'Deseleccionar todo',
      noProductsWarning: 'Seleccione al menos un producto para incluir en el correo.',
      fileTypesLegend: 'Adjuntar documentos',
      fileTypesLoading: 'Cargando documentos disponibles…',
      fileTypes: {
        study: 'Estudios',
        marketing: 'Documentos de marketing',
        quality: 'Documentos de calidad',
      },
      qualityHint: 'Los documentos de calidad no se adjuntan. {email} los envía por separado a los destinatarios.',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Settings                                                           */
  /* ------------------------------------------------------------------ */
  settings: {
    title: 'Configuración',
    subtitle: 'Configure las preferencias de la aplicación',
    multiCart: {
      title: 'Múltiples documentos',
      description:
        'Active múltiples documentos de reunión para organizar productos para diferentes clientes o reuniones. Puede tener hasta 3 documentos y alternar entre ellos desde el encabezado.',
    },
    favoritesOnHome: {
      title: 'Favoritos en el panel',
      description: 'Muestre su sección de favoritos en la pantalla de inicio para acceder rápidamente a los productos guardados.',
    },
    favoriteGroups: {
      title: 'Grupos de favoritos',
      description: 'Organice sus favoritos en grupos. Puede crear hasta 5 grupos y arrastrar productos entre ellos. Cada grupo se puede añadir a un documento con un clic.',
    },
    productData: {
      title: 'Datos de productos',
      description: 'Muestra cuándo se cargaron por última vez los datos de productos desde el servidor.',
      lastLoaded: 'Última carga',
      never: 'Nunca cargado',
      reload: 'Recargar productos',
      reloading: 'Recargando...',
      reloadSuccess: 'Datos de productos recargados',
      reloadError: 'No se pudieron recargar los datos de productos',
    },
    changePassword: {
      title: 'Cambiar contraseña',
      description: 'Actualice la contraseña que utiliza para iniciar sesión.',
      currentPassword: 'Contraseña actual',
      newPassword: 'Nueva contraseña',
      confirmPassword: 'Confirmar nueva contraseña',
      submit: 'Actualizar contraseña',
      submitting: 'Actualizando...',
      success: 'Su contraseña ha sido actualizada.',
      passwordMismatch: 'Las contraseñas no coinciden',
      error: 'No se pudo actualizar la contraseña',
      backToSettings: 'Volver a la configuración',
    },
  },

  /* ------------------------------------------------------------------ */
  /* Wizard                                                             */
  /* ------------------------------------------------------------------ */
  wizard: {
    steps: {
      country: 'País',
      application: 'Aplicación',
      indication: 'Indicación',
    },
    selectCountry: 'Seleccionar país',
    selectCountrySubtitle: '¿En qué países debe estar disponible el producto?',
    selectApplication: 'Seleccionar aplicación',
    selectApplicationSubtitle: '¿Qué forma de dosificación prefiere?',
    selectIndication: 'Seleccionar indicación',
    selectIndicationSubtitle: '¿Qué áreas de salud son relevantes?',
    showResults: 'Mostrar resultados',
    noSelection: 'Sin selección',
    selected: (count: number) => `${count} seleccionados`,
    filterAnd: 'Y',
    filterOr: 'O',
    filterAndExplanation: 'Los productos deben cumplir todos los criterios seleccionados',
    filterOrExplanation: 'Los productos deben cumplir al menos un criterio por paso',
  },

  /* ------------------------------------------------------------------ */
  /* Filter sidebar                                                     */
  /* ------------------------------------------------------------------ */
  filterSidebar: {
    title: 'Filtro',
    category: 'Categoría',
    country: 'País',
    application: 'Aplicación',
    indication: 'Indicación',
    partner: 'Socio',
    marketingClaims: 'Declaraciones de marketing',
    agreement: 'Acuerdo',
    agreements: {
      DA: 'DA — Acuerdo de distribución',
      CPA: 'CPA — Protección del cliente',
      Gentleman: 'Gentleman Agreement',
      LOI: 'LOI — Carta de intención',
      none: 'Sin acuerdo',
    },
    agreementDescriptions: {
      DA: 'Acuerdo de distribución (exclusivo)',
      CPA: 'Acuerdo de protección del cliente (no exclusivo)',
      Gentleman: 'Gentleman Agreement (verbal, sin contrato)',
      LOI: 'Carta de intención (etapa preliminar del DA)',
      none: 'Aún no se ha firmado un acuerdo para este país',
    },
    newProducts: 'Nuevos productos',
    newStudy: 'Nuevo estudio',
    restrictedTo: (territories: string) => `Tu vista está limitada a ${territories}.`,
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------ */
  /* Not Found (404)                                                    */
  /* ------------------------------------------------------------------ */
  notFound: {
    title: '404',
    heading: 'Página no encontrada',
    description: 'La página que busca no existe o ha sido movida.',
    goBack: 'Volver',
    home: 'Inicio',
  },

  /* ------------------------------------------------------------------ */
  /* Forgot Password                                                    */
  /* ------------------------------------------------------------------ */
  forgotPassword: {
    subtitle: 'Restablecer su contraseña',
    email: 'Correo electrónico',
    emailPlaceholder: 'su@correo.com',
    submit: 'Enviar enlace de restablecimiento',
    submitting: 'Enviando...',
    success: 'Hemos enviado un enlace para restablecer la contraseña a su dirección de correo electrónico.',
    backToLogin: 'Volver al inicio de sesión',
    error: 'No se pudo enviar el enlace de restablecimiento',
  },

  /* ------------------------------------------------------------------ */
  /* Reset Password                                                     */
  /* ------------------------------------------------------------------ */
  resetPassword: {
    subtitle: 'Establecer una nueva contraseña',
    email: 'Correo electrónico',
    newPassword: 'Nueva contraseña',
    confirmPassword: 'Confirmar contraseña',
    submit: 'Restablecer contraseña',
    submitting: 'Restableciendo...',
    passwordMismatch: 'Las contraseñas no coinciden',
    backToLogin: 'Volver al inicio de sesión',
    error: 'No se pudo restablecer la contraseña',
  },

  /* ------------------------------------------------------------------ */
  /* Marketing claim descriptions                                       */
  /* ------------------------------------------------------------------ */
  claimDescriptions: {
    Vegan: 'No contiene ingredientes ni subproductos de origen animal.',
    Organic: 'Certificado orgánico: cultivado sin pesticidas ni fertilizantes sintéticos.',
    'Non-GMO': 'Libre de organismos genéticamente modificados.',
    'Clinically Studied': 'Eficacia respaldada por ensayos clínicos publicados.',
    Patented: 'Protegido por una o más patentes por su formulación o proceso único.',
    Halal: 'Certificado Halal: cumple con las pautas dietéticas islámicas.',
    Kosher: 'Certificado Kosher: cumple con las leyes dietéticas judías.',
    'Gluten-Free': 'No contiene gluten: apto para personas con enfermedad celíaca o sensibilidad al gluten.',
  },
  pdf: {
    productDataSheet: 'Ficha de producto',
    productDataSheets: 'Fichas de producto',
    keyFacts: 'Datos clave',
    dailyDosage: 'Dosis diaria',
    appearance: 'Apariencia',
    bioactiveIngredient: 'Ingrediente bioactivo',
    indications: 'Indicaciones',
    possibleApplications: 'Aplicaciones posibles',
    description: 'Descripcion',
    linkStudies: 'Estudios',
    origin: 'Origen',
    confidential: 'Confidencial -- Solo para uso comercial',
    productCountOne: ':count producto',
    productCountOther: ':count productos',
    clinicalStudies: 'Estudios clinicos',
    marketingClaims: 'Declaraciones de marketing',
    territories: 'Territorios',
    moq: 'Cantidad minima de pedido en kg',
    shelfLife: 'Vida util',
    shelfLifeValue: ':months meses',
    novelFoodStatus: 'Novel Food',
    badgeNew: 'NUEVO',
    badgeNewStudy: 'NUEVO ESTUDIO',
    contactPerson: 'Su contacto en Vivatis:',
    website: 'Sitio web del producto',
    multimedia: 'Sitio web multimedia',
  },
} as const;

export default es;
